import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

// Async thunks for API calls
export const fetchPlcs = createAsyncThunk(
  'dataSources/fetchPlcs',
  async (_, { rejectWithValue }) => {
    try {
      return await api.dataSources.getAllPlcs();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchChannels = createAsyncThunk(
  'dataSources/fetchChannels',
  async (_, { rejectWithValue }) => {
    try {
      return await api.dataSources.getAllChannels();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchTags = createAsyncThunk(
  'dataSources/fetchTags',
  async (_, { rejectWithValue }) => {
    try {
      return await api.dataSources.getAllTags();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRealTimeData = createAsyncThunk(
  'dataSources/fetchRealTimeData',
  async (tagIds, { rejectWithValue }) => {
    try {
      return await api.dataSources.getRealTimeTagData(tagIds);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const writeTagValue = createAsyncThunk(
  'dataSources/writeTagValue',
  async ({ tagId, value }, { rejectWithValue }) => {
    try {
      const result = await api.dataSources.writeTagValue(tagId, value);
      return { tagId, value, success: result.success, timestamp: new Date().toISOString() };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const dataSourcesSlice = createSlice({
  name: 'dataSources',
  initialState: {
    // Data entities
    plcs: [],
    channels: [],
    tags: [],
    opcServers: [],
    sockets: [],
    webSockets: [],
    
    // Real-time data
    realTimeData: {},
    connectionStatus: {},
    
    // UI state
    loading: {
      plcs: false,
      channels: false,
      tags: false,
      realTimeData: false
    },
    
    // Error handling
    error: {
      plcs: null,
      channels: null,
      tags: null,
      realTimeData: null
    },
    
    // Statistics
    statistics: {
      totalTags: 0,
      activeTags: 0,
      connectedPlcs: 0,
      updatesPerSecond: 0,
      lastUpdate: null
    }
  },
  
  reducers: {
    // PLC Management
    addPlc: (state, action) => {
      state.plcs.push(action.payload);
    },
    
    updatePlc: (state, action) => {
      const index = state.plcs.findIndex(plc => plc.plcId === action.payload.plcId);
      if (index !== -1) {
        state.plcs[index] = { ...state.plcs[index], ...action.payload };
      }
    },
    
    removePlc: (state, action) => {
      state.plcs = state.plcs.filter(plc => plc.plcId !== action.payload);
    },

    // Channel Management
    addChannel: (state, action) => {
      state.channels.push(action.payload);
    },
    
    updateChannel: (state, action) => {
      const index = state.channels.findIndex(channel => channel.channelId === action.payload.channelId);
      if (index !== -1) {
        state.channels[index] = { ...state.channels[index], ...action.payload };
      }
    },
    
    removeChannel: (state, action) => {
      state.channels = state.channels.filter(channel => channel.channelId !== action.payload);
    },

    // Tag Management
    addTag: (state, action) => {
      state.tags.push(action.payload);
    },
    
    updateTag: (state, action) => {
      const index = state.tags.findIndex(tag => tag.tagId === action.payload.tagId);
      if (index !== -1) {
        state.tags[index] = { ...state.tags[index], ...action.payload };
      }
    },
    
    removeTag: (state, action) => {
      state.tags = state.tags.filter(tag => tag.tagId !== action.payload);
    },

    // Real-time data updates
    updateTagValue: (state, action) => {
      const { tagId, value, timestamp, quality } = action.payload;
      state.realTimeData[tagId] = { 
        value, 
        timestamp: timestamp || new Date().toISOString(),
        quality: quality || 'Good'
      };
    },
    
    updateMultipleTagValues: (state, action) => {
      const updates = action.payload;
      updates.forEach(update => {
        state.realTimeData[update.tagId] = {
          value: update.value,
          timestamp: update.timestamp || new Date().toISOString(),
          quality: update.quality || 'Good'
        };
      });
    },

    // Connection status
    updateConnectionStatus: (state, action) => {
      const { deviceId, status, lastSeen } = action.payload;
      state.connectionStatus[deviceId] = { 
        status, 
        lastSeen: lastSeen || new Date().toISOString() 
      };
    },
    
    updateMultipleConnectionStatus: (state, action) => {
      const updates = action.payload;
      Object.entries(updates).forEach(([deviceId, status]) => {
        state.connectionStatus[deviceId] = {
          ...status,
          lastSeen: status.lastSeen || new Date().toISOString()
        };
      });
    },

    // Statistics
    updateStatistics: (state, action) => {
      state.statistics = { ...state.statistics, ...action.payload };
    },

    // Error management
    clearError: (state, action) => {
      const errorType = action.payload;
      state.error[errorType] = null;
    },
    
    clearAllErrors: (state) => {
      state.error = {
        plcs: null,
        channels: null,
        tags: null,
        realTimeData: null
      };
    },

    // Reset state
    resetDataSources: (state) => {
      return {
        ...state.initialState,
        loading: { plcs: false, channels: false, tags: false, realTimeData: false },
        error: { plcs: null, channels: null, tags: null, realTimeData: null }
      };
    }
  },
  
  extraReducers: (builder) => {
    // PLCs
    builder
      .addCase(fetchPlcs.pending, (state) => {
        state.loading.plcs = true;
        state.error.plcs = null;
      })
      .addCase(fetchPlcs.fulfilled, (state, action) => {
        state.loading.plcs = false;
        state.plcs = action.payload;
        state.statistics.connectedPlcs = action.payload.filter(plc => plc.connectionStatus === 'connected').length;
      })
      .addCase(fetchPlcs.rejected, (state, action) => {
        state.loading.plcs = false;
        state.error.plcs = action.payload;
      });

    // Channels
    builder
      .addCase(fetchChannels.pending, (state) => {
        state.loading.channels = true;
        state.error.channels = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.loading.channels = false;
        state.channels = action.payload;
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.loading.channels = false;
        state.error.channels = action.payload;
      });

    // Tags
    builder
      .addCase(fetchTags.pending, (state) => {
        state.loading.tags = true;
        state.error.tags = null;
      })
      .addCase(fetchTags.fulfilled, (state, action) => {
        state.loading.tags = false;
        // Transform backend field names to frontend field names
        // API returns camelCase with uppercase ID: tagID, channelID, plcID, etc.

        // Helper to safely convert to string (handles objects, null, undefined)
        const safeStr = (val) => {
          if (val === null || val === undefined) return '';
          if (typeof val === 'object') return JSON.stringify(val);
          return String(val);
        };

        // Helper to safely convert to number
        const safeNum = (val) => {
          if (val === null || val === undefined) return 0;
          if (typeof val === 'object') return 0;
          return Number(val) || 0;
        };

        state.tags = (action.payload || []).map(tag => ({
          tagId: safeNum(tag.tagID ?? tag.TagID ?? tag.tagId),
          tagName: safeStr(tag.tagName ?? tag.TagName),
          channelId: safeNum(tag.channelID ?? tag.ChannelID ?? tag.channelId),
          channelName: safeStr(tag.channelName ?? tag.ChannelName) || `Channel ${safeNum(tag.channelID ?? tag.ChannelID ?? tag.channelId)}`,
          plcId: safeNum(tag.plcID ?? tag.PlcID ?? tag.plcId),
          majorAddress: safeNum(tag.majorAddress ?? tag.MajorAddress),
          minorAddress: safeNum(tag.minorAddress ?? tag.MinorAddress),
          dbNo: safeNum(tag.dbNo ?? tag.DBNo),
          type: safeNum(tag.type ?? tag.Type),
          dataType: safeNum(tag.dataType ?? tag.DataType),
          moduleId: safeNum(tag.moduleID ?? tag.ModuleID ?? tag.moduleId),
          formule: safeStr(tag.formule ?? tag.Formule),
          unit: safeStr(tag.unit ?? tag.Unit),
          alarm: safeStr(tag.alarm ?? tag.Alarm),
          alarmCondition: safeStr(tag.alarmCondition ?? tag.AlarmCondition),
          dbWriteInterval: safeNum(tag.dbWriteInterval ?? tag.DbWriteInterval) || 5000,
          currentValue: safeStr(tag.value ?? tag.Value ?? tag.currentValue) || '0',
          quality: safeStr(tag.quality ?? tag.Quality) || 'Good',
          lastUpdate: safeStr(tag.lastUpdate ?? tag.LastUpdate)
        }));
        state.statistics.totalTags = state.tags.length;
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading.tags = false;
        state.error.tags = action.payload;
      });

    // Real-time data
    builder
      .addCase(fetchRealTimeData.pending, (state) => {
        state.loading.realTimeData = true;
        state.error.realTimeData = null;
      })
      .addCase(fetchRealTimeData.fulfilled, (state, action) => {
        state.loading.realTimeData = false;
        // Update real-time data with API response
        action.payload.forEach(tagData => {
          state.realTimeData[tagData.tagId] = {
            value: tagData.value,
            timestamp: tagData.timestamp,
            quality: tagData.quality || 'Good'
          };
        });
        state.statistics.activeTags = Object.values(state.realTimeData).filter(data => data.quality === 'Good').length;
        state.statistics.lastUpdate = new Date().toISOString();
      })
      .addCase(fetchRealTimeData.rejected, (state, action) => {
        state.loading.realTimeData = false;
        state.error.realTimeData = action.payload;
      });

    // Write tag value
    builder
      .addCase(writeTagValue.fulfilled, (state, action) => {
        const { tagId, value, timestamp } = action.payload;
        state.realTimeData[tagId] = {
          value,
          timestamp,
          quality: 'Good'
        };
      });
  }
});

export const {
  addPlc,
  updatePlc,
  removePlc,
  addChannel,
  updateChannel,
  removeChannel,
  addTag,
  updateTag,
  removeTag,
  updateTagValue,
  updateMultipleTagValues,
  updateConnectionStatus,
  updateMultipleConnectionStatus,
  updateStatistics,
  clearError,
  clearAllErrors,
  resetDataSources
} = dataSourcesSlice.actions;

// Selectors
export const selectPlcs = (state) => state.dataSources.plcs;
export const selectChannels = (state) => state.dataSources.channels;
export const selectTags = (state) => state.dataSources.tags;
export const selectRealTimeData = (state) => state.dataSources.realTimeData;
export const selectConnectionStatus = (state) => state.dataSources.connectionStatus;
export const selectStatistics = (state) => state.dataSources.statistics;
export const selectLoading = (state) => state.dataSources.loading;
export const selectErrors = (state) => state.dataSources.error;

// Complex selectors
export const selectPlcById = (state, plcId) => 
  state.dataSources.plcs.find(plc => plc.plcId === plcId);

export const selectChannelsByPlc = (state, plcId) => 
  state.dataSources.channels.filter(channel => channel.plcId === plcId);

export const selectTagsByChannel = (state, channelId) => 
  state.dataSources.tags.filter(tag => tag.channelId === channelId);

export const selectConnectedPlcs = (state) => 
  state.dataSources.plcs.filter(plc => plc.connectionStatus === 'connected');

export const selectActiveDataSources = (state) => ({
  plcs: state.dataSources.plcs.filter(plc => plc.isActive).length,
  opcServers: state.dataSources.opcServers.filter(server => server.isActive).length,
  sockets: state.dataSources.sockets.filter(socket => socket.isActive).length,
  webSockets: state.dataSources.webSockets.filter(ws => ws.isActive).length
});

export default dataSourcesSlice.reducer;